import _ from 'underscore';
import React, { useRef } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { usePost } from 'hooks/use-post';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';

import Dialog from 'components/dialog';
import NumberField from 'components/number-field';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import { postCalculateNutrients } from 'views/feed-management/features/manual-feeds/api/calculate-nutrients';
import { fieldList } from 'views/feed-management/features/manual-feeds/components/calculate-nutrients-fields';
import { CalculateNutrientInput } from 'views/feed-management/features/manual-feeds/types/calculate-nutrients-input.type';
import { CalculateNutrientsResult } from 'views/feed-management/features/manual-feeds/types/calculate-nutrients-result.type';

type CalculateNutrientsProps = {
  manualFeedId: string;
};

const PAGE_NAME_ID = 'feedManagement.editManualFeed.calculateNutrients';
const CalculateNutrients: React.FC<CalculateNutrientsProps> = ({ manualFeedId }) => {
  const intl = useIntl();
  const formFields = fieldList(intl);
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();
  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);

  const totalRows = Math.ceil(formFields.length / 2);

  const defaultValues = Object.keys({} as CalculateNutrientInput).reduce((acc, key) => ({ ...acc, [key]: undefined }), {});

  const { control, handleSubmit, reset } = useForm<CalculateNutrientInput>({
    defaultValues: defaultValues,
    shouldUnregister: false,
  });

  const handleCalculateNutrients = (request: CalculateNutrientInput) => {
    const req = {} as CalculateNutrientInput;
    Object.keys(request).forEach((key) => {
      req[key as keyof CalculateNutrientInput] = Number(request[key as keyof CalculateNutrientInput]);
    });
    calculateNutrients.mutate({ request: req });
  };

  //#region POST DATA
  const calculateNutrients = usePost<CalculateNutrientsResult, { request: CalculateNutrientInput }>(
    (request) => postCalculateNutrients(request.request, manualFeedId),
    () => {
      reset();
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      dialogRef?.current?.setOpen(false);
      queryClient.invalidateQueries({
        queryKey: ['getManualFeed']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  //#endregion POST DATA

  //#region HANDLERS
  const onKeyDown = (e: any) => e.stopPropagation();

  const parseValue = (value: number) => parseFloat(String(value).replace(/,/g, ''));
  //#endregion HANDLERS

  return (
    <Box width={{ xs: '100%', md: 'auto' }} flexDirection={'column'} display={'flex'} gap={3} margin={5}>
      <Dialog
        maxWidth="md"
        ref={dialogRef}
        onClose={reset}
        sx={{ zIndex: 1510 }}
        onKeyDown={(e) => e.key === 'Tab' && e.stopPropagation()}
        title={intl.formatMessage({ id: `${PAGE_NAME_ID}.title` })}
        button={<Button>{intl.formatMessage({ id: `${PAGE_NAME_ID}.title` })}</Button>}>
        <Box
          gap={4}
          display={'grid'}
          gridAutoFlow={'column'}
          gridTemplateColumns={'repeat(2, 1fr)'}
          gridTemplateRows={`repeat(${totalRows}, auto)`}>
          {formFields.map((field, idx) => (
            <NumberField
              dark
              key={idx}
              fullWidth
              label={field.name}
              onKeyDown={onKeyDown}
              controller={{
                name: field.value,
                control: control,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    between: (value: number) =>
                      parseValue(value) >= field.min && parseValue(value) <= field.max
                        ? true
                        : intl.formatMessage({ id: 'form.rules.between' }, { lower: field.min, upper: field.max }),
                  },
                },
              }}
            />
          ))}
        </Box>
        <Box display={'flex'} justifyContent={'end'} pt={4}>
          <Button type="button" onClick={handleSubmit(handleCalculateNutrients)}>
            <FormattedMessage id={`${PAGE_NAME_ID}.calculate`} />
            <CircularProgress size={18} color="info" sx={{ display: calculateNutrients.isPending ? 'block' : 'none' }} />
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default React.memo(CalculateNutrients);
